import { UNGROUPED_RESOURCES_GROUP_ID } from '@/console/features/groups/components/group-list/group-list.constants';

import { api } from '../../_generated/api/assets-api.generated';

export const vaultsApi = api.enhanceEndpoints({
  addTagTypes: ['Vault', 'Secret', 'SecretUnceiled', 'Groups'],
  endpoints: {
    ReadAllVaults: {
      providesTags: (result, _, args) => {
        const tags = [{ type: 'Vault' as const, id: 'LIST' }];

        if (result?.readAllVaults) {
          result?.readAllVaults.items.forEach((vault) => {
            tags.push({ type: 'Vault' as const, id: vault.vaultId });
          });
        }

        const filteredGroup = args?.filter?.groupId;
        const groupId = filteredGroup?.contains ?? UNGROUPED_RESOURCES_GROUP_ID;
        if (filteredGroup) {
          return [...tags, { type: 'Groups' as const, id: groupId }];
        }

        return tags;
      },
    },
    // Shares the list tag with ReadAllVaults, so the vault mutations refresh
    // it too.
    ReadVaultsForSelect: {
      providesTags: [{ type: 'Vault' as const, id: 'LIST' }],
    },
    CreateVault: {
      invalidatesTags: [{ type: 'Vault', id: 'LIST' }, 'Groups' as const],
    },
    DeleteVault: {
      invalidatesTags: [{ type: 'Vault', id: 'LIST' }, 'Groups' as const],
    },
    ReadVault: {
      providesTags: (_, __, arg) => [{ type: 'Vault', id: arg.vaultId }],
    },
    ReadSecretsForVault: {
      providesTags: (_, __, arg) => [{ type: 'Vault', id: arg.vaultId }],
    },
    CreateSecret: {
      invalidatesTags: (_, __, arg) => [
        { type: 'Vault', id: arg.secret.vaultId },
        { type: 'Vault', id: 'LIST' },
        'Groups' as const,
      ],
    },
    UpdateVault: {
      invalidatesTags: (_, __, arg) => [
        { type: 'Vault', id: 'LIST' },
        { type: 'Vault', id: arg.vaultId },
        'Groups' as const,
      ],
    },
    UpdateSecret: {
      invalidatesTags: (result) => [
        { type: 'Vault', id: result?.updateSecret.vaultId },
        { type: 'Secret', id: result?.updateSecret.secretId },
        { type: 'SecretUnceiled', id: result?.updateSecret.secretId },
        { type: 'Vault', id: 'WEAK_SECRETS_SUMMARY' },
      ],
    },
    DeleteSecret: {
      invalidatesTags: (result) => {
        const tags = [
          { type: 'Vault' as const, id: 'LIST' },
          'Groups' as const,
        ];

        if (result?.deleteSecret) {
          return [
            ...tags,
            { type: 'Vault' as const, id: result?.deleteSecret.vaultId },
          ];
        }

        return tags;
      },
    },
    ReadSecret: {
      providesTags: (_, __, arg) => [{ type: 'Secret', id: arg.secretId }],
    },
    ReadSecretUnceiled: {
      providesTags: (_, __, arg) => [
        { type: 'SecretUnceiled', id: arg.secretId },
      ],
    },
    ReadWeakSecretsSummary: {
      providesTags: (result) => {
        const tags = [{ type: 'Vault' as const, id: 'LIST' }];

        if (result?.readAllVaults) {
          result?.readAllVaults.items.forEach((vault) => {
            tags.push({ type: 'Vault' as const, id: vault.vaultId });
          });
        }

        return tags;
      },
    },
  },
});

export const {
  useReadAllVaultsQuery,
  useReadVaultsForSelectQuery,
  useCreateVaultMutation,
  useDeleteVaultMutation,
  useReadVaultQuery,
  useReadSecretsForVaultQuery,
  useCreateSecretMutation,
  useDeleteSecretMutation,
  useReadSecretQuery,
  useReadSecretUnceiledQuery,
  useUpdateVaultMutation,
  useUpdateSecretMutation,
  useReadAllVaultNamesForPolicyRowQuery,
  useReadWeakSecretsSummaryQuery,
} = vaultsApi;
